/** @jsxImportSource @emotion/react */
import { Breakpoint, useTheme } from "basis";
import {
  Children,
  createContext,
  ReactElement,
  ReactNode,
  useContext,
  useMemo,
} from "react";

const NUMBERS_ONLY_REGEX = /^\d+$/;

const addPxIfNeeded = (str: string): string => {
  if (NUMBERS_ONLY_REGEX.test(str)) {
    return `${str}px`;
  }

  return str;
};

type ContextValue = {
  switchLayoutAt: Breakpoint;
};

const FormActionsContext = createContext<ContextValue>({
  switchLayoutAt: "sm",
});

interface Props {
  switchLayoutAt?: Breakpoint;
  children: [ReactElement, ReactElement];
}

export const FormActions = ({ switchLayoutAt = "sm", children }: Props) => {
  const theme = useTheme();
  const childrenHaveWidth = Children.toArray(children).some(
    (child) => (child as ReactElement).props.width !== undefined,
  );
  const contextValue = useMemo(
    () => ({
      switchLayoutAt,
    }),
    [switchLayoutAt],
  );

  return (
    <div
      css={{
        display: "grid",
        rowGap: theme.space[4],
        [theme.minMediaQueries[switchLayoutAt]]: {
          gridTemplateColumns: "auto auto",
          columnGap: theme.space[8],
          ...(childrenHaveWidth ? { justifyContent: "start" } : {}),
        },
      }}
    >
      <FormActionsContext.Provider value={contextValue}>
        {children}
      </FormActionsContext.Provider>
    </div>
  );
};

interface PrimaryProps {
  width?: string;
  children: ReactNode;
}

const Primary = ({ width, children }: PrimaryProps) => {
  const theme = useTheme();
  const { switchLayoutAt } = useContext(FormActionsContext);

  return (
    <div
      css={{
        display: "grid",
        [theme.minMediaQueries[switchLayoutAt]]: {
          width: width ? addPxIfNeeded(width) : undefined,
        },
      }}
    >
      {children}
    </div>
  );
};

FormActions.Primary = Primary;

interface SecondaryProps {
  width?: string;
  children: ReactNode;
}
const Secondary = ({ width, children }: SecondaryProps) => {
  const theme = useTheme();
  const { switchLayoutAt } = useContext(FormActionsContext);

  return (
    <div
      css={{
        display: "grid",
        [theme.minMediaQueries[switchLayoutAt]]: {
          width: width ? addPxIfNeeded(width) : undefined,
          gridRow: 1,
        },
      }}
    >
      {children}
    </div>
  );
};

FormActions.Secondary = Secondary;
